//#region src/reserved-platform-prefixes.ts
/**
* PLATFORM-RESERVED `/v1/*` PREFIXES — the single source of truth.
*
* ⛔ **WHY THIS IS ITS OWN MODULE, AND WHY IT IS IN `@zion/sdk-contracts`.** The list was declared in
* `@zion/sdk-cp-dp-contracts`'s `api-routes.ts` until #1748. It had to move DOWN the dependency graph
* because a SECOND enforcement point now needs it: `createAppSchema`'s slug denylist
* (`app-admin-inputs.ts`), which lives here. `@zion/sdk-contracts` imports NO `@zion/*` package and
* must keep it that way, whereas `@zion/sdk-cp-dp-contracts` already depends ON this package
* (`workspace:*`) — so the only direction that works is down. `api-routes.ts` now IMPORTS and
* RE-EXPORTS it, so every pre-existing import site (`@zion/sdk-cp-dp-contracts`'s barrel and
* `@zion/sdk-node`'s `api-path-rules.ts`) keeps working unchanged.
* ⚠️ **THE PRE-EXISTING IMPORT SITES, ENUMERATED AS MEASURED (2026-08-17) — the earlier list was
* WRONG IN BOTH DIRECTIONS.** It read *"`@zion/sdk-node`'s `api-path-rules.ts`, the operator,
* release-engine"*. Measured: the operator (`app-api-route-renderer.ts:73-76`) and release-engine
* (`bundle-parser.ts:65-68`) import `validatePrefix` / `rejectReservedOverlap` — the FUNCTIONS — and
* never the array, so naming them overstated the blast radius; while `@zion/sdk-node`'s
* `api-path-rules.ts:6-14` really does import `RESERVED_PLATFORM_PREFIXES` and iterate it, so
* dropping it would have understated the radius. ⇒ The ARRAY's non-test importers are exactly: this
* package's barrel, `@zion/sdk-cp-dp-contracts`'s `api-routes.ts` (which re-exports it) and ITS
* barrel, and `@zion/sdk-node`'s `api-path-rules.ts`. `app-admin-inputs.ts` imports the DERIVED
* `RESERVED_PLATFORM_PREFIX_SLUGS`, not the array.
* ⭐ The functional claim the old enumeration was supporting is unaffected and still holds: every
* pre-existing import site keeps working unchanged, because the re-export preserves the specifier.
* ⛔ **There is exactly ONE array. Do not add a second copy anywhere** — under-inclusion in a copy is
* a vulnerability (see below), and two lists cannot be kept in agreement by convention.
*
* ⛔ **A DEDICATED LEAF MODULE, not folded into an existing one.** It is imported by
* `app-admin-inputs.ts` (slug denylist) and re-exported across a package boundary; a leaf with zero
* imports of its own can be consumed from either place with no risk of an import cycle, which
* `event-subjects.ts` (already imported BY `app-admin-inputs.ts`) and
* `fixture-api-path-namespace-rules.ts` (which imports `fixture-placement-rules.js`) cannot promise.
* The list is also not *about* events or fixture rules; it is platform routing policy.
*/
/**
* Platform-reserved `/v1/*` prefixes. App fixtures MUST NOT declare any
* apiPath that overlaps with any entry here.
*
* Audited 2026-05-13 against `cloud/dp/core-api/src/**\/*.controller.ts`.
* Maintenance rule: whenever core-api adds a top-level `/v1/*`
* `@Controller` prefix, add it here. The list is the security boundary
* for app fixtures — over-inclusion is safe; under-inclusion is a
* vulnerability (a malicious app could declare `apiPaths: ['/v1/auth']`
* and intercept authentication traffic).
*
* Enforced at THREE layers (defense in depth):
*   1. CP app CREATE (`createAppSchema`, both copies — `app-admin-inputs.ts` here and the
*      release-engine DTO): a slug EQUAL to a reserved prefix's own segment is refused, because
*      #1733 confines every apiPath to `/v1/<slug>` and `rejectReservedOverlap` then refuses every
*      path such an app could possibly declare. Added #1748 — see the slug refine for why an
*      immutable slug makes this unrepairable after the fact.
*   2. CP publish (`@zion/release-engine-v3` bundle-parser) — primary line,
*      rejects bad bundles before they reach any DP.
*   3. DP operator reconcile (`@zion/zion-operator`) — catches stale
*      bundles published before the reserved list was tightened.
*/
const RESERVED_PLATFORM_PREFIXES = [
	"/v1/auth",
	"/v1/setup",
	"/v1/admin",
	"/v1/release",
	"/v1/platform",
	"/v1/apps",
	"/v1/fixtures",
	"/v1/me",
	"/v1/internal",
	"/v1/csp-reports"
];
/**
* The reserved prefixes as bare app-slug tokens — `/v1/auth` → `auth`.
*
* ⛔ **DERIVED, never re-typed.** A hand-written second list of these strings is the exact
* duplicated-invariant shape `SLUG_PATTERN` and `RESERVED_APP_SLUG_MESSAGE` were collapsed to fix
* (#1107, CR-8): the copy would silently stop matching the day a prefix is added to the array above,
* and the failure is invisible — an app gets created with a slug that can never publish an apiPath.
*
* ⚠️ Deliberately a `Set` of the FINAL segment-free token, so the slug refine is exact equality and
* never a prefix test: an app slugged `authz` is perfectly legal (`/v1/authz` does not overlap
* `/v1/auth` — `prefixesOverlap` is segment-aware), and a `startsWith` here would refuse it.
*/
const RESERVED_PLATFORM_PREFIX_SLUGS = new Set(RESERVED_PLATFORM_PREFIXES.map((p) => p.slice(4)));
//#endregion
export { RESERVED_PLATFORM_PREFIXES, RESERVED_PLATFORM_PREFIX_SLUGS };

//# sourceMappingURL=reserved-platform-prefixes.js.map